import { useId, type ReactNode } from 'react'
import { Check, Minus, Plus } from 'lucide-react'

/**
 * PRIMITIVES DE FORMULAIRE LIVE CONTROL (LOT 9.0.2).
 *
 * Complète le design system existant (Select premium, lc-input, Floating)
 * avec ce qui manquait : Field, NumberInput à unité intégrée, Checkbox et
 * Switch custom, cartes de canaux. Règles :
 *   · JAMAIS un contrôle natif visible qui casse le design ;
 *   · JAMAIS au prix de l'accessibilité : rôles ARIA, clavier (Space/
 *     Enter), focus visible, cibles tactiles ≥ 40 px, reduced-motion ;
 *   · pas de dépendance UI ajoutée — quelques dizaines de lignes chacune.
 * Documentation : docs/phase2-lot9-0-2-interaction-form-polish-report.md.
 */

/* ─── Field : label AU-DESSUS, helper/erreur EN DESSOUS ─── */
export function Field(props: {
  label: string
  helper?: string | undefined
  error?: string | null | undefined
  children: ReactNode
}) {
  return (
    <div className="lc-field">
      <span className="lc-label">{props.label}</span>
      {props.children}
      {props.error
        ? <span className="lc-helper lc-helper--error" role="alert">{props.error}</span>
        : props.helper ? <span className="lc-helper">{props.helper}</span> : null}
    </div>
  )
}

/* ─── TextInput : l'input de référence (styles lc-input existants) ─── */
export function TextInput(props: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  maxLength?: number
  autoFocus?: boolean
  ariaLabel?: string
  /** Champ affiché mais non modifiable — l'identité canonique, par exemple. */
  disabled?: boolean
  /** « date » : sélecteur natif, affiché au format du navigateur (jj/mm/aaaa
   *  en français) ; la valeur reste AAAA-MM-JJ. Texte par défaut. */
  type?: 'text' | 'date'
  max?: string
}) {
  return (
    <input
      className="lc-input"
      type={props.type ?? 'text'}
      max={props.max}
      value={props.value}
      placeholder={props.placeholder}
      maxLength={props.maxLength}
      autoFocus={props.autoFocus}
      disabled={props.disabled}
      aria-label={props.ariaLabel}
      onChange={(e) => props.onChange(e.target.value)}
    />
  )
}

/* ─── NumberInput : unité INTÉGRÉE au contrôle, steppers optionnels ─── */
export function NumberInput(props: {
  value: string | number
  onChange: (raw: string) => void
  unit?: string | null | undefined
  min?: number
  max?: number
  step?: number
  /** −/+ : seulement quand l'incrément a du sens (durées), pas partout. */
  steppers?: boolean
  width?: number
  ariaLabel: string
  placeholder?: string
}) {
  const step = props.step ?? 1
  const bump = (dir: 1 | -1) => {
    const cur = Number(props.value) || 0
    let next = cur + dir * step
    if (props.min !== undefined) next = Math.max(props.min, next)
    if (props.max !== undefined) next = Math.min(props.max, next)
    props.onChange(String(next))
  }
  return (
    <div className={`lc-numin${props.steppers ? ' lc-numin--steppers' : ''}`} style={props.width ? { width: props.width } : undefined}>
      {props.steppers ? (
        <button type="button" className="lc-numin__step" aria-label="Diminuer" onClick={() => bump(-1)}>
          <Minus size={14} aria-hidden="true" />
        </button>
      ) : null}
      <input
        type="number" inputMode="decimal"
        value={props.value}
        min={props.min} max={props.max} step={step}
        placeholder={props.placeholder ?? '…'}
        aria-label={props.ariaLabel}
        onChange={(e) => props.onChange(e.target.value)}
      />
      {props.unit ? <span className="lc-numin__unit" aria-hidden="true">{props.unit}</span> : null}
      {props.steppers ? (
        <button type="button" className="lc-numin__step" aria-label="Augmenter" onClick={() => bump(1)}>
          <Plus size={14} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}

/* ─── Checkbox custom (~21 px), ligne entière cliquable en variante Row ─── */
export function Checkbox(props: {
  checked: boolean
  onChange: (v: boolean) => void
  label?: ReactNode
  disabled?: boolean | undefined
  ariaLabel?: string | undefined
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={props.checked}
      aria-label={props.ariaLabel}
      disabled={props.disabled}
      className="lc-checkbox"
      onClick={() => props.onChange(!props.checked)}
    >
      <span className={`lc-checkbox__box${props.checked ? ' lc-checkbox__box--on' : ''}`} aria-hidden="true">
        <Check size={14} strokeWidth={3} className="lc-checkbox__mark" />
      </span>
      {props.label ? <span className="lc-checkbox__label">{props.label}</span> : null}
    </button>
  )
}

/* ─── Switch : la sémantique ON/OFF (activer/désactiver) ─── */
export function Switch(props: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean | undefined
  ariaLabel?: string | undefined
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.checked}
      aria-label={props.ariaLabel}
      disabled={props.disabled}
      className={`lc-switch2${props.checked ? ' lc-switch2--on' : ''}`}
      onClick={() => props.onChange(!props.checked)}
    >
      <span className="lc-switch2__thumb" aria-hidden="true" />
    </button>
  )
}

/** Ligne complète cliquable : titre + description + Switch à droite. */
export function SwitchRow(props: {
  checked: boolean
  onChange: (v: boolean) => void
  title: string
  desc?: string
  disabled?: boolean
}) {
  const id = useId()
  return (
    <div
      className={`lc-switchrow${props.disabled ? ' lc-switchrow--disabled' : ''}`}
      onClick={(e) => {
        // La ligne entière bascule — sauf si le clic vient déjà du switch.
        if ((e.target as HTMLElement).closest('.lc-switch2')) return
        if (!props.disabled) props.onChange(!props.checked)
      }}
    >
      <span className="lc-stack" style={{ gap: 2, minWidth: 0 }}>
        <span className="lc-switchrow__title" id={id}>{props.title}</span>
        {props.desc ? <span className="lc-helper">{props.desc}</span> : null}
      </span>
      <Switch checked={props.checked} onChange={props.onChange} disabled={props.disabled} ariaLabel={props.title} />
    </div>
  )
}

/* ─── Carte de canal : le choix multi-sélection qui se voit ─── */
export function ChannelCard(props: {
  checked: boolean
  onChange: (v: boolean) => void
  icon: ReactNode
  label: string
  note?: string | null | undefined
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={props.checked}
      disabled={props.disabled}
      className={`lc-chancard${props.checked ? ' lc-chancard--on' : ''}`}
      onClick={() => props.onChange(!props.checked)}
    >
      <span className="lc-chancard__icon" aria-hidden="true">{props.icon}</span>
      <span className="lc-stack" style={{ gap: 1, minWidth: 0, textAlign: 'left' }}>
        <span className="lc-chancard__label">{props.label}</span>
        {props.note ? <span className="lc-helper lc-chancard__note">{props.note}</span> : null}
      </span>
      <span className={`lc-checkbox__box lc-chancard__box${props.checked ? ' lc-checkbox__box--on' : ''}`} aria-hidden="true">
        <Check size={13} strokeWidth={3} className="lc-checkbox__mark" />
      </span>
    </button>
  )
}

/* ─── Tokens du composer de condition ─── */
/** Partie FIXE de la phrase (la donnée surveillée) — un token non éditable. */
export function SentenceToken(props: { children: ReactNode }) {
  return <span className="lc-token">{props.children}</span>
}
